// Copyright 2020 TOKU Systems Inc.

import React from 'react';
import { ApiKeyRecord } from './common';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  InputAdornment,
  IconButton,
  DialogActions,
  Button,
  Icon,
} from '@material-ui/core';
import copy from 'copy-to-clipboard';
import { useSnackbar } from 'notistack';

interface ShowApiKeyDialogProps {
  record?: ApiKeyRecord;
  onClose: () => void;
}

export default function ShowApiKeyDialog(
  props: ShowApiKeyDialogProps
): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();

  const handleCopyTokenClick = (): void => {
    const token = props.record?.token;

    if (token !== undefined) {
      if (copy(token, { format: 'text/plain' })) {
        enqueueSnackbar('Token copied to clipboard', {
          variant: 'info',
        });
      } else {
        enqueueSnackbar(
          'Failed to copy to clipboard. Try to manually copy & paste.',
          {
            variant: 'warning',
          }
        );
      }
    } else {
      enqueueSnackbar('Nothing to copy.', { variant: 'warning' });
    }
  };

  return (
    <Dialog open={props.record !== undefined} onClose={props.onClose}>
      {props.record?.token !== undefined && (
        <React.Fragment>
          <DialogTitle>Copy token</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Below is the token (JWT) to use when sending requests to the
              Illumass API.
            </DialogContentText>
            <DialogContentText style={{ fontWeight: 'bold' }}>
              COPY AND SAVE THE TOKEN NOW. The token will not be shown again
              after this window is closed.
            </DialogContentText>
            <TextField
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopyTokenClick}>
                      <Icon>file_copy</Icon>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
              value={props.record?.token ?? ''}
            />
          </DialogContent>
        </React.Fragment>
      )}
      <DialogTitle>Details</DialogTitle>
      <DialogContent>
        <TextField
          InputProps={{ readOnly: true }}
          fullWidth
          value={props.record?.description ?? '???'}
          helperText="Description"
        />
        <TextField
          InputProps={{ readOnly: true }}
          fullWidth
          value={props.record?.issuedAt ?? '???'}
          helperText="Issued at"
        />
        <TextField
          InputProps={{ readOnly: true }}
          fullWidth
          value={props.record?.expiresAt ?? '???'}
          helperText="Expires at"
        />
        <TextField
          InputProps={{ readOnly: true }}
          fullWidth
          value={props.record?.fingerprint ?? '???'}
          helperText="Fingerprint"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
